import React from 'react';
import { Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  TableCell,
  TableRow,
  IconButton,
  StatusChip,
  DeltaChip,
  MoreVertIcon,
} from '@occc/ui-design-system';
import { DateTimeFormat, formatDateTime } from '@occc-app/lib/date';
import { formatMoneyAmount } from '@occc-app/lib/currency';

import { PastSubmission } from '../scan-data-weekly-file.types';
import {
  pastSubmissionsFileStatusToColorMap,
  pastSubmissionsFileStatusToLabelMap,
  pastSubmissionsTypeToLabelMap,
} from '../scan-data-weekly-file.constants';
import { routeCreator } from '../../../scan-data.router';

type PastSubmissionsTableRowProps = {
  data: PastSubmission;
};

type MenuItemType = 'viewSubmission';

export const PastSubmissionsTableRow = ({ data }: PastSubmissionsTableRowProps) => {
  const navigate = useNavigate();
  const [actionsForUuid, setActionsForUuid] = React.useState<string | null>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    setActionsForUuid(null);
  };

  const getHandlerForAdditionalActions =
    (uuid: string) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(uuid);
    };

  const formattedDate = formatDateTime(new Date(data.submissionDate), DateTimeFormat.DateDashed);

  const handleRowClick = () => {
    navigate(routeCreator.pastSubmissionView({ date: formattedDate, type: data.type }));
  };

  const getMenuItemAction =
    (menuItemName: MenuItemType) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      if (menuItemName === 'viewSubmission') {
        navigate(routeCreator.pastSubmissionView({ date: formattedDate, type: data.type }));
      }

      handleClose(event);
    };

  return (
    <TableRow onClick={handleRowClick} sx={{ cursor: 'pointer' }}>
      <TableCell>
        {formatDateTime(new Date(data.submissionDate), DateTimeFormat.DateWithYear)}
      </TableCell>
      <TableCell>{pastSubmissionsTypeToLabelMap[data.type]}</TableCell>
      <TableCell>
        <Stack direction='row' alignItems='center'>
          {data.transactionsReported}
          <DeltaChip value={data.transactionsReportedChangePercent} />
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction='column'>
          <Typography variant='subtitle2'>{data.submissionGroups.total} groups</Typography>
          <Typography
            variant='caption'
            color={
              data.submissionGroups.submitted === data.submissionGroups.total
                ? 'textSecondary'
                : 'error'
            }
          >
            {data.submissionGroups.submitted}/{data.submissionGroups.total} submitted
          </Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <StatusChip
          label={pastSubmissionsFileStatusToLabelMap[data.fileStatus]}
          variant={pastSubmissionsFileStatusToColorMap[data.fileStatus]}
        />
      </TableCell>
      <TableCell>{formatMoneyAmount(data.estimatedReimbursement, { showCents: true })}</TableCell>
      <TableCell align='right'>
        <IconButton onClick={getHandlerForAdditionalActions(data.uuid)} sx={{ p: 0 }}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          open={data.uuid === actionsForUuid}
          onClose={handleClose}
          anchorEl={anchorEl}
          PaperProps={{
            sx: {
              borderRadius: '4px',
            },
          }}
        >
          <MenuItem onClick={getMenuItemAction('viewSubmission')}>View Submission</MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
};
